/**
 * Project items — the unified table holding both parts and sub-parts.
 *
 * Discriminated by `itemType`:
 *   - PART: parent_item_id is null; sr_number required; part_quantity required;
 *           may carry client_unit_price and (when no sub-parts) supplier_unit_price.
 *   - SUB_PART: parent_item_id points to a PART; quantity_per_parent required;
 *               supplier_unit_price required; no sr_number, no client_unit_price.
 *
 * Variant rules:
 *   - A PART with children (sub-parts) cannot carry direct order batches.
 *   - A PART with direct order batches cannot acquire children.
 *   These invariants are enforced in service code and (where possible) by DB triggers.
 */

import { Type, type Static } from '@sinclair/typebox';
import { ItemTypes } from '../enums.js';
import { FIELD_LIMITS, NUMERIC_LIMITS } from '../constants.js';
import {
  PaisaSchema,
  RowVersionField,
  SoftDeleteFields,
  TimestampFields,
  UuidSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Read shape — stored fields
// ---------------------------------------------------------------------------

export const ProjectItemSchema = Type.Object({
  id: UuidSchema,
  projectId: UuidSchema,
  /** Null for top-level parts; set to the parent part's id for sub-parts. */
  parentItemId: Type.Union([UuidSchema, Type.Null()]),
  itemType: Type.Union(ItemTypes.map((t) => Type.Literal(t))),

  /** Sr# — present on parts and sub-parts. Part Sr# is unique within the project; sub-part Sr# is unique within its parent. */
  srNumber: Type.Union([
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.SR_NUMBER_MAX }),
    Type.Null(),
  ]),
  /** Part # / Sub-part #. Optional because legacy rows may not have one. */
  itemNumber: Type.Union([Type.String({ maxLength: FIELD_LIMITS.ITEM_NUMBER_MAX }), Type.Null()]),
  /** Noun — kept as `name` on the wire for compatibility with existing code. */
  name: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX }),
  /** Unit of measure. Parts only; null on sub-parts. */
  uom: Type.Union([Type.String({ maxLength: FIELD_LIMITS.UOM_MAX }), Type.Null()]),
  /** GST percentage for parts, stored as basis points. Null on sub-parts. */
  gstRateBps: Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
    Type.Null(),
  ]),

  /** Required quantity for the part. Null on sub-parts. */
  partQuantity: Type.Union([
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),
    Type.Null(),
  ]),
  /** Sub-parts only: how many of this sub-part are required per parent. */
  quantityPerParent: Type.Union([
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),
    Type.Null(),
  ]),

  /** Client billing price per unit of the PART. Null on sub-parts. */
  clientUnitPricePaisa: Type.Union([PaisaSchema, Type.Null()]),
  /** Supplier cost per unit. Required on sub-parts and on parts without sub-parts;
   *  may be null on parts that have sub-parts (cost rolls up from sub-parts). */
  supplierUnitPricePaisa: Type.Union([PaisaSchema, Type.Null()]),

  /** Schedule of Stores item-level make/brand. Optional on parts and sub-parts. */
  makeBrand: Type.Union([Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX }), Type.Null()]),
  /** Schedule of Stores item-level origin. Optional on parts and sub-parts. */
  origin: Type.Union([Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX }), Type.Null()]),
  /** Schedule of Stores item-level remarks. Optional on parts and sub-parts. */
  remarks: Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()]),

  ...TimestampFields,
  ...SoftDeleteFields,
  ...RowVersionField,
});
export type ProjectItem = Static<typeof ProjectItemSchema>;

// ---------------------------------------------------------------------------
// Read shape with derived fields
// ---------------------------------------------------------------------------

export const ProjectItemWithStatusSchema = Type.Composite([
  ProjectItemSchema,
  Type.Object({
    /** Computed: parent_part.qty × quantity_per_parent for sub-parts; same as partQuantity for parts. */
    totalRequiredQuantity: Type.Integer({ minimum: 0 }),
    /** Sum across active batches. */
    activeOrderedQuantity: Type.Integer({ minimum: 0 }),
    /** Highest stage number with a recorded date/outcome (or 0 if none). */
    currentStageNumber: Type.Integer({ minimum: 0, maximum: 18 }),
    /** Whether all batches have been in-house inspected — the aggregate gate predicate. */
    isReadyForPartStages: Type.Boolean(),
    /** Days the entity has been in its current stage. */
    daysInCurrentStage: Type.Integer({ minimum: 0 }),
    /** For sub-parts: a partial indicator label ("2 of 3 batches arrived"), null otherwise. */
    partialBatchIndicator: Type.Union([Type.String(), Type.Null()]),
    /** PART only: cumulative delivered quantity recorded via delivery events. */
    deliveredQuantity: Type.Integer({ minimum: 0 }),
    /** PART only: remaining quantity to deliver. */
    remainingDeliveryQuantity: Type.Integer({ minimum: 0 }),
    /** PART only: cumulative invoice amount recorded via payment events. */
    invoicedAmountPaisa: PaisaSchema,
    /** PART only: remaining amount to invoice. */
    remainingInvoiceAmountPaisa: PaisaSchema,
    /** PART only: cumulative cheque released amount. */
    chequeReleasedAmountPaisa: PaisaSchema,
    /** PART only: cumulative cheque cleared amount. */
    chequeClearedAmountPaisa: PaisaSchema,
    /** PART only: remaining amount to clear. */
    remainingPaymentAmountPaisa: PaisaSchema,
    /** Whether this part has sub-parts (variant A). Null on sub-parts. */
    hasSubParts: Type.Union([Type.Boolean(), Type.Null()]),
    /** PART only: quantity × unit price before GST. Null on sub-parts. */
    valuePaisa: Type.Union([PaisaSchema, Type.Null()]),
    /** PART only: value × GST %. Null if no GST % is present or item is a sub-part. */
    gstAmountPaisa: Type.Union([PaisaSchema, Type.Null()]),
    /** PART only: value + GST amount. Null if GST amount cannot be derived or item is a sub-part. */
    totalValuePaisa: Type.Union([PaisaSchema, Type.Null()]),
  }),
]);
export type ProjectItemWithStatus = Static<typeof ProjectItemWithStatusSchema>;

// ---------------------------------------------------------------------------
// Create / update inputs
// ---------------------------------------------------------------------------

export const ProjectItemPartCreateSchema = Type.Object({
  itemType: Type.Literal('PART'),
  srNumber: Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.SR_NUMBER_MAX }),
  itemNumber: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.ITEM_NUMBER_MAX }),
  name: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX }),
  uom: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.UOM_MAX }),
  partQuantity: Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),
  clientUnitPricePaisa: PaisaSchema,
  gstRateBps: Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
  supplierUnitPricePaisa: Type.Optional(PaisaSchema),
  makeBrand: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX }), Type.Null()])),
  origin: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX }), Type.Null()])),
  remarks: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()])),
});
export type ProjectItemPartCreate = Static<typeof ProjectItemPartCreateSchema>;

export const ProjectItemSubPartCreateSchema = Type.Object({
  itemType: Type.Literal('SUB_PART'),
  parentItemId: UuidSchema,
  srNumber: Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.SR_NUMBER_MAX }),
  itemNumber: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.ITEM_NUMBER_MAX }),
  name: Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX }),
  quantityPerParent: Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),
  supplierUnitPricePaisa: Type.Optional(PaisaSchema),
  makeBrand: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX }), Type.Null()])),
  origin: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX }), Type.Null()])),
  remarks: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()])),
});
export type ProjectItemSubPartCreate = Static<typeof ProjectItemSubPartCreateSchema>;

export const ProjectItemCreateSchema = Type.Union([
  ProjectItemPartCreateSchema,
  ProjectItemSubPartCreateSchema,
]);
export type ProjectItemCreate = Static<typeof ProjectItemCreateSchema>;

export const ProjectItemUpdateSchema = Type.Object({
  srNumber: Type.Optional(Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.SR_NUMBER_MAX })),
  itemNumber: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.ITEM_NUMBER_MAX }), Type.Null()])),
  name: Type.Optional(Type.String({ minLength: 1, maxLength: FIELD_LIMITS.NAME_MAX })),
  uom: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.UOM_MAX }), Type.Null()])),
  partQuantity: Type.Optional(Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX })),
  quantityPerParent: Type.Optional(
    Type.Integer({ minimum: 1, maximum: NUMERIC_LIMITS.QUANTITY_MAX }),
  ),
  clientUnitPricePaisa: Type.Optional(Type.Union([PaisaSchema, Type.Null()])),
  gstRateBps: Type.Optional(Type.Union([
    Type.Integer({ minimum: 0, maximum: NUMERIC_LIMITS.PERCENT_BPS_MAX }),
    Type.Null(),
  ])),
  supplierUnitPricePaisa: Type.Optional(Type.Union([PaisaSchema, Type.Null()])),
  makeBrand: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.MAKER_NAME_BRAND_MAX }), Type.Null()])),
  origin: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.ORIGIN_MAX }), Type.Null()])),
  remarks: Type.Optional(Type.Union([Type.String({ maxLength: FIELD_LIMITS.NOTES_MAX }), Type.Null()])),
  /**
   * Admin-only acknowledgement required when reducing partQuantity below
   * the currently-active ordered quantity (spec §6.3). The first PATCH
   * attempt without this flag fails with a 409 carrying the warning detail;
   * the UI surfaces a confirm dialog and retries with the flag set to true.
   */
  acknowledgeReducedQuantity: Type.Optional(Type.Boolean()),
  rowVersion: Type.Integer({ minimum: 0 }),
});
export type ProjectItemUpdate = Static<typeof ProjectItemUpdateSchema>;
